import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { skeleton } from "@/lib/quizFactory";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { QuizCard, GamesTable } from "@/components/admin/QuizCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { QuickSetupDialog } from "@/components/admin/QuickSetupDialog";
import { GameResultsDialog } from "@/components/admin/GameResultsDialog";
import { AdminsDialog } from "@/components/admin/AdminsDialog";

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [quizzes, setQuizzes] = useState(null);
  const [games, setGames] = useState([]);
  const [setupOpen, setSetupOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [adminsOpen, setAdminsOpen] = useState(false);

  const load = () => {
    api.get("/quizzes").then((r) => setQuizzes(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/games").then((r) => setGames(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const host = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/games", { quiz_id: id });
      nav(`/host/${data.id}`);
    } catch (e) {
      toast.error(`${t("hosting_error")}: ${errMsg(e)}`);
    } finally { setBusy(null); }
  };
  const create = async (v) => {
    setBusy("new");
    try {
      const { data } = await api.post("/quizzes", skeleton(v));
      nav(`/admin/quiz/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const duplicate = async (id) => { await api.post(`/quizzes/${id}/duplicate`); load(); };
  const archive = async (id, archived) => {
    try {
      await api.post(`/quizzes/${id}/archive?archived=${archived}`);
      toast.success(archived ? t("archived_toast") : t("unarchived_toast"));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [gameToDelete, setGameToDelete] = useState(null);
  const removeGame = async () => { await api.delete(`/games/${gameToDelete.id}`); setGameToDelete(null); load(); };
  const remove = async () => { await api.delete(`/quizzes/${toDelete.id}`); setToDelete(null); load(); };
  const openResults = async (id) => {
    setResults(null); setResultsOpen(true);
    try { const { data } = await api.get(`/games/${id}/results`); setResults(data); }
    catch (e) { toast.error(errMsg(e)); setResultsOpen(false); }
  };

  return (
    <div className="min-h-screen admin-grid-bg">
      <AdminHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t("my_quizzes")}</h1>
            <p className="text-muted-foreground mt-2">{t("quizzes_sub")}</p>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "owner" && (
              <Button variant="outline" data-testid="manage-admins-button" className="h-11" onClick={() => setAdminsOpen(true)}><Shield className="w-4 h-4 mr-1.5" />{t("manage_admins")}</Button>
            )}
            <Button data-testid="new-quiz-button" className="font-bold h-11" onClick={() => setSetupOpen(true)}><Plus className="w-4 h-4 mr-1.5" />{t("new_quiz")}</Button>
          </div>
        </div>
        {quizzes && quizzes.length === 0 && <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">{t("no_quizzes")}</div>}
        {quizzes && quizzes.length > 0 && (() => {
          const mineActive = quizzes.filter((q) => q.mine && !q.archived);
          const mineArchived = quizzes.filter((q) => q.mine && q.archived);
          const shared = quizzes.filter((q) => !q.mine && q.access !== "no_access");
          const rest = quizzes.filter((q) => !q.mine && q.access === "no_access");
          const isOwner = user?.role === "owner";
          const grid = (list, empty) => (
            list.length === 0
              ? <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">{empty}</div>
              : <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="quiz-list">
                  {list.map((q) => (
                    <QuizCard key={q.id} quiz={q} t={t} hosting={busy === q.id} onHost={() => host(q.id)} onEdit={() => nav(`/admin/quiz/${q.id}`)}
                      onDuplicate={() => duplicate(q.id)} onDelete={() => setToDelete(q)} onArchive={() => archive(q.id, !q.archived)} />
                  ))}
                </div>
          );
          return (
            <Tabs defaultValue="mine" data-testid="quiz-tabs">
              <TabsList className="h-auto flex-wrap">
                <TabsTrigger value="mine" data-testid="tab-mine">{t("tab_mine")} ({mineActive.length})</TabsTrigger>
                <TabsTrigger value="shared" data-testid="tab-shared">{t("tab_shared")} ({shared.length})</TabsTrigger>
                {isOwner && <TabsTrigger value="rest" data-testid="tab-rest">{t("tab_rest")} ({rest.length})</TabsTrigger>}
                <TabsTrigger value="archive" data-testid="tab-archive">{t("tab_archive")} ({mineArchived.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="mine" className="mt-6">{grid(mineActive, t("no_quizzes"))}</TabsContent>
              <TabsContent value="shared" className="mt-6">{grid(shared, t("no_shared"))}</TabsContent>
              {isOwner && <TabsContent value="rest" className="mt-6">{grid(rest, t("no_rest"))}</TabsContent>}
              <TabsContent value="archive" className="mt-6">{grid(mineArchived, t("no_archived"))}</TabsContent>
            </Tabs>
          );
        })()}
        <h2 className="text-lg font-bold mt-14 mb-4">{t("recent_games")}</h2>
        <GamesTable games={games} t={t} lang={lang} onOpen={(id) => nav(`/host/${id}`)} onResults={openResults} onDelete={setGameToDelete} />
      </main>
      <QuickSetupDialog open={setupOpen} onOpenChange={setSetupOpen} onCreate={create} t={t} busy={busy === "new"} />
      <GameResultsDialog open={resultsOpen} onOpenChange={setResultsOpen} data={results} loading={!results} t={t} lang={lang} />
      <AdminsDialog open={adminsOpen} onOpenChange={setAdminsOpen} t={t} />
      <AlertDialog open={!!gameToDelete} onOpenChange={(o) => !o && setGameToDelete(null)}>
        <AlertDialogContent data-testid="delete-game-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_game")}</AlertDialogTitle>
            <AlertDialogDescription>{gameToDelete ? `${gameToDelete.title} · ${gameToDelete.pin}` : ""}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-500" data-testid="delete-game-confirm-button" onClick={removeGame}>{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.title} — {t("delete_confirm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-quiz-button" onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white">{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
